import type {
  BrowserClientMessage,
  BrowserServerMessage,
} from '@repo/contracts/browser-protocol';

/**
 * The seam between the browse UI and a browser worker.
 *
 * Two implementations speak it: `ws-transport.ts` (ticket from the API, then a
 * socket to the worker container) and `mock-transport.ts` (a simulated worker
 * that runs entirely in the tab, used by tests and by `VITE_BROWSER_MOCK=true`).
 * Both emit the same `BrowserServerMessage`s off @repo/contracts, so nothing
 * above this file — the session hook, the toolbar, the capture rail — knows
 * which one it is talking to.
 */

/**
 * What went wrong, at session level. Lives here rather than in the session hook
 * because a transport needs to name one (a 401 minting a ticket is
 * `unauthorized`, not a generic outage) and importing the hook from a transport
 * would be a cycle.
 */
export type FailureKind = 'busy' | 'crashed' | 'unauthorized' | 'unreachable';

export interface TransportHandlers {
  /** A protocol message from the worker. */
  onMessage: (message: BrowserServerMessage) => void;
  /** The socket closed. `code` is the worker's close code (4401/4409/4410) or
   *  a standard one; `clean` is false when the connection dropped mid-session. */
  onClose: (info: { code: number; clean: boolean }) => void;
  /** The session never came up. Distinct from onClose because there is nothing
   *  to retry *within* the session — the whole connection has to start again. */
  onFailure: (message: string, kind?: FailureKind) => void;
}

export interface BrowserTransport {
  send: (message: BrowserClientMessage) => void;
  close: () => void;
}

export interface TransportOptions {
  handlers: TransportHandlers;
}

export type TransportFactory = (options: TransportOptions) => BrowserTransport;

/**
 * True when the simulated worker should be used instead of a real one.
 *
 * Opt-*in*, so forgetting to set anything talks to the real worker rather than
 * silently showing a fake page that looks like it works. Component tests stub
 * this env var; the Playwright suite sets it on its dev server.
 */
export function shouldMockWorker(): boolean {
  return import.meta.env.VITE_BROWSER_MOCK === 'true';
}
